// ** React Imports
import { useState, useMemo } from 'react'

// ** MUI Imports
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LoginIcon from '@mui/icons-material/Login';
import { styled } from '@mui/material/styles'
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useLocation } from 'react-router-dom'
import BlockIcon from '@mui/icons-material/Block';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { Link } from 'react-router-dom'
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from "@mui/material";
import Swal from 'sweetalert2'; 
import TextField from "@mui/material/TextField";
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import TableSortLabel from '@mui/material/TableSortLabel';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';


const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  // height: 26,
  height: 21,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Moi-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const getSearchableValue = (row, column) => {
    const value = row[column.id];

    if (value === null || value === undefined) {
        return '';
    }
    if (column.id === 'user_plan' && Array.isArray(value)) {
        return value[0]?.plan?.name || '';
    }
    if (column.id === 'manager' && typeof value === 'object' && value !== null) {
        return value.name || '';
    }
    if (column.id === 'surveyName' && row.survey?.name) {
        return row.survey.name;
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
        return value.name || '';
    }
    
    return String(value);
};

const SortIcon = ({ direction }) => {
    const iconStyle = {
    fontSize: '1rem',
    ml: 0.5,
    color: 'rgba(117,117,117,0.28)',
  };
  if (direction === 'asc') {
    return <ArrowUpwardIcon sx={iconStyle} />;
  }
  return <ArrowDownwardIcon sx={iconStyle} />;
};

const TableStickyHeader = ({columns, rows, handleEdit, handleDelete, handleStatus,handleReminder,handleUserLogin,handleEndSession,isLoading}) => {
  const authUser = localStorage.getItem('authUser');
  const user = JSON.parse(authUser);

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const location = useLocation();
  const current_path = location.pathname;

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }  

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleSortRequest = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
    setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
  };
 
  const handleInvalidStatus = () => {
    Swal.fire('Please subscribe to a plan to activate the company!')
  }

  const processedRows = useMemo(() => {
    let filteredRows = [...rows];

    if (searchQuery) {
      filteredRows = filteredRows.filter(row =>
        columns.some(column => {
          const searchableValue = getSearchableValue(row, column);
          return searchableValue.toLowerCase().includes(searchQuery.toLowerCase());
        })
      );
    }

    if (sortConfig.key) {
      filteredRows.sort((a, b) => {
        const aValue = getSearchableValue(a, { id: sortConfig.key });
        const bValue = getSearchableValue(b, { id: sortConfig.key });

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredRows;
  }, [rows, searchQuery, sortConfig, columns]);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          size='small'
          placeholder='Search...'
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ width: { xs: '60%', sm: '40%', md: '25%' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label='sticky table'>
        {columns.length > 0 ? 
        <>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell 
                  key={column.id} 
                  align={column.align} 
                  size='small' 
                  sx={{ minWidth: column.minWidth, cursor: 'pointer' }}
                  onClick={() => handleSortRequest(column.id)}
                >
                  <TableSortLabel
                    active={sortConfig.key === column.id}
                    direction={sortConfig.key === column.id ? sortConfig.direction : 'asc'}
                    IconComponent={(props) => <SortIcon {...props} direction={sortConfig.direction} />}
                    disabled={column.id === 'action' || column.id === 'status' || column.id === 'login' || column.id === 'reminder'}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          { isLoading ? 
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress/>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody> 
          :
          processedRows.length > 0 ? 
          <TableBody>
            {processedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => {
              return (
                <TableRow hover role='checkbox' tabIndex={-1} key={row.id || row.code}>
                  {columns.map(column => {
                    const value = row[column.id]

                    return (
                      <TableCell key={column.id} align={column.align} size='small'>
                        
                        {
                          column.id === 'action' ?                          
                          (user.id === row.userId || current_path==='/users' || current_path==='/plans' || current_path==='/companies'   || (current_path === '/settings_survey' && user.id === row.createdBy)) ? 
                          (
                            current_path === '/sessions' ? 
                            (
                              <>{row.endDate === null ? 
                                <>
                                <Link to={"/invite-user/"+row.id} > 
                                  <Tooltip title="Invite Users" size='small' className='ss_edit' sx={{ marginRight: 2 }}> 
                                    <IconButton size='small'><EditIcon /></IconButton> 
                                  </Tooltip>
                                </Link>
                                <Link to={"/reminder/"+row.id} > 
                                  <Tooltip title="Click to view completion status" size='small' className='ss_view' sx={{ marginRight: 2 }}> 
                                    <IconButton size='small'><SendIcon /></IconButton> 
                                  </Tooltip>
                                </Link>
                                <Tooltip onClick={() => handleEndSession(row.id)} title="End Session" style={{cursor:'pointer'}} sx={{ marginRight: 1.5 }}>
                                    <BlockIcon />
                                </Tooltip>
                                </>
                                :""
                              }
                              </> 
                            ):
                              <>  
                              <Tooltip onClick={() => handleEdit(row.id)} title="Edit" size='small' className='ss_edit' sx={{ marginRight: 1.5 }}>
                                <IconButton >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip> 
                              {current_path !== '/plans' ?
                                <Tooltip onClick={() => handleDelete(row.id)} title="Delete" size='small' className='ss_delete'>
                                  <IconButton>
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip> : 
                              "" }
                            </>                              
                          ) :                           
                          ''
                          :
                          column.id === 'status' ? 
                          ( current_path.includes('reminder') ? 
                            (row.status === 1 ? 
                            <span className="badge rounded-pill bg-success">Completed</span> : 
                            <span className="badge rounded-pill bg-warning">Pending</span>)
                          :
                          // current_path.includes('companies') ?
                          //   <IOSSwitch size='small' onChange={(e) => row.user_plan.length === 0 ? handleInvalidStatus() : handleStatus(e, row.id)} defaultChecked={row.status === '1' ? true : false}/>
                          //   :
                            <IOSSwitch size='small' onChange={(e) => handleStatus(e, row.id)} defaultChecked={row.status === '1' ? true : false}/>)
                          :
                          column.id === 'manager' ?
                          (value !== null && typeof value === 'object' ? value.name : '')
                          :
                          column.id === 'reminder'? 
                            row.status === 1 ? '' :                             
                            <Tooltip style={{color:'White'}} size='small' title={"Send Reminder to "+(row.user_name?.name || 'user')} variant='contained' onClick={(e) => handleReminder(e, row.userId,row.manager_name?.id,row.user_name?.name)} className='ss_view'>
                              <IconButton>
                              <ForwardToInboxIcon />
                              </IconButton>
                            </Tooltip>
                          :
                          column.id === 'login' ? 
                          <Tooltip size='small' onClick={() => handleUserLogin(row.email)} title="Login" className='ss_view'>
                            <IconButton>
                              <LoginIcon />
                            </IconButton>
                          </Tooltip>
                          :
                          column.id === 'surveyName' ? 
                         (row.survey && row.survey.name ? row.survey.name:"")
                          : 
                          column.id === 'updatedAt' ? 
                            row.status === 1 ? row.updatedAt : '-'
                          : 
                          (value !== null && typeof value === 'object' ? 
                            (Array.isArray(value) ? (value.length > 0 && value[0].plan ? value[0].plan.name : '') : value.name) 
                            : value)
                        } 
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
          :
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                {searchQuery ? `No results found for "${searchQuery}"` : "No data available"}
              </TableCell>
            </TableRow>
          </TableBody>
        }
        </>
        :
        <TableBody>
          <TableRow>
            <TableCell colSpan={10} align="center">Please define columns</TableCell>
          </TableRow>
        </TableBody>
        }
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={processedRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}

export default TableStickyHeader
